/**
 * First-match tips: one-line hints in the alert slot (or under it). Each one shows at most once,
 * for about 20 s (`TIP_MS`) from the moment it first appears — then it is done, whether or not the
 * player did the thing; doing the thing first also ends it. Done is remembered in this browser
 * (localStorage; private windows and blocked storage simply show them again next time).
 *   push  — RIGHT-CLICK LAND TO PUSH YOUR BORDER (from the start)
 *   silo  — 3 · BUILD A SILO TO STRIKE BACK (at weapons release, without a way to strike)
 *   brace — R · BRACE — CITY TAKES NO DAMAGE FOR 20 S (under the first city threat)
 *   critical — ONE MORE HIT ELIMINATES YOU — BRACE OR INTERCEPT (the first time our capital is critical)
 *   diplo — HOLD RIGHT-CLICK ON A NATION FOR DIPLOMACY (from 1:00 until release)
 * Besides the tips, `pushed` remembers our first push ever (the in-world RMB · PUSH cue stops then).
 */
export type TipId = 'push' | 'silo' | 'brace' | 'critical' | 'diplo' | 'pushed';

/** a tip is on screen this long (from its first frame), then never again (ms) */
export const TIP_MS = 20_000;

const KEY = 'sa.tips.v1';

type TipStorage = Pick<Storage, 'getItem' | 'setItem'>;

function browserStorage(): TipStorage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

export class Tips {
  private done = new Set<TipId>();
  /** when each tip first showed (this session; ms) */
  private since = new Map<TipId, number>();

  constructor(private storage: TipStorage | null = browserStorage()) {
    try {
      const raw = storage?.getItem(KEY);
      if (raw) for (const id of JSON.parse(raw) as TipId[]) this.done.add(id);
    } catch {
      // unreadable: show the tips again
    }
  }

  /** Not done yet (never shown out, never done by the player)? */
  pending(id: TipId): boolean {
    return !this.done.has(id);
  }

  /**
   * The tip may be on screen now: true while pending and within `TIP_MS` of its first showing
   * (the first call starts that clock); once the time is up it is done for good.
   */
  show(id: TipId, nowMs: number): boolean {
    if (this.done.has(id)) return false;
    const since = this.since.get(id);
    if (since === undefined) {
      this.since.set(id, nowMs);
      return true;
    }
    if (nowMs - since < TIP_MS) return true;
    this.complete(id);
    return false;
  }

  /** Done (the player did it, or it had its time): never show this tip again. */
  complete(id: TipId): void {
    if (this.done.has(id)) return;
    this.done.add(id);
    try {
      this.storage?.setItem(KEY, JSON.stringify([...this.done]));
    } catch {
      // storage full or blocked: remembered for this session only
    }
  }
}
